// src/components/VaccineAdjustmentForm.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Select,
  Textarea,
  VStack,
  Grid,
  GridItem,
  HStack,
  Flex,
  Text,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  Checkbox,
  Divider,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Link,
} from "@chakra-ui/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSyringe,
  faTable,
  faPaperclip,
  faShieldHalved,
  faLock,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import CustomDatePicker from "./CustomDatePicker";
import * as IconButton from "../themes/IconButtonSet";
import { StandardHeader } from "../themes/StandardHeader";

// ไฟล์แนบถูกเสิร์ฟโดยเซิร์ฟเวอร์ API ไม่ใช่เครื่องที่รันหน้าเว็บ
// จึงต้องอ้างที่อยู่ของ API เมื่อไม่ได้ตั้ง REACT_APP_BASE_URL ไว้
const BASE_URL =
  process.env.REACT_APP_BASE_URL || process.env.REACT_APP_API_URL || "http://localhost:3000";

const budgetTypeOptions = [
  { value: "Distribution", label: "งบจำหน่าย" },
  { value: "Prevention", label: "งบป้องกัน" },
];

const requestTypeOptions = [
  "ตรวจนับประจำปี",
  "ตรวจนับกรณีพิเศษ",
  "แก้ไขยอดที่คลาดเคลื่อนจากระบบ",
  "วัคซีนชำรุดหรือเสียหาย",
  "วัคซีนหมดอายุ",
  "อื่น ๆ",
];

const approvalStatusInfo = {
  Pending: { label: "รออนุมัติ", scheme: "orange" },
  Approved: { label: "ปรับยอดแล้ว", scheme: "green" },
  Rejected: { label: "ไม่อนุมัติ", scheme: "red" },
  Cancelled: { label: "ยกเลิก", scheme: "gray" },
  Archived: { label: "เอกสารเก่า", scheme: "purple" },
};

const formatNumber = (value) => Number(value || 0).toLocaleString("th-TH");

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("th-TH");
};

const formatFileSize = (bytes) => {
  if (!bytes) {
    return "-";
  }
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.ceil(bytes / 1024)} KB`;
};

/**
 * ผลต่างระหว่างจำนวนที่นับได้กับยอดในระบบ พร้อมสีที่สื่อทิศทาง
 */
const DifferenceTag = ({ difference }) => {
  if (difference === 0) {
    return <Text color="gray.500">ตรงกัน</Text>;
  }
  const isIncrease = difference > 0;
  return (
    <Text color={isIncrease ? "green.600" : "red.600"} fontWeight="semibold">
      {isIncrease ? "+" : "-"}
      {formatNumber(Math.abs(difference))}
    </Text>
  );
};

const VaccineAdjustmentForm = () => {
  const navigate = useNavigate();
  const { transactionId } = useParams();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const cancelRef = useRef();

  const isNew = !transactionId;

  const [header, setHeader] = useState({
    RequestDate: new Date(),
    WarehouseID: "",
    BudgetType: "Distribution",
    RequestType: "",
    RequestTypeOther: "",
    ReferenceDocumentType: "",
    ReferenceDocumentNo: "",
    WarehouseStaffID: "",
    RequestDetail: "",
    Remark: "",
  });

  const [adjustment, setAdjustment] = useState(null);
  const [savedDetails, setSavedDetails] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isInventoryAdmin, setIsInventoryAdmin] = useState(false);
  const [currentUserID, setCurrentUserID] = useState("");

  const [warehouses, setWarehouses] = useState([]);
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [isInventoryLoading, setIsInventoryLoading] = useState(false);

  const [lines, setLines] = useState([]);
  const [newLine, setNewLine] = useState({ VaccineID: "", LotID: "", RequestQuantity: "", Remark: "" });
  const [manualLot, setManualLot] = useState(false);

  const [confirmedQuantities, setConfirmedQuantities] = useState({});
  const [editMode, setEditMode] = useState(false);

  const [pendingFiles, setPendingFiles] = useState([]);
  const [documentType, setDocumentType] = useState("หนังสืออนุมัติ");
  const [isUploading, setIsUploading] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [dialog, setDialog] = useState({ isOpen: false, type: null });
  const [notify, setNotify] = useState({
    isOpen: false,
    title: "",
    message: "",
    status: "info",
    onCloseCallback: null,
  });

  const showNotify = (title, message, status = "info", onCloseCallback = null) => {
    setNotify({ isOpen: true, title, message, status, onCloseCallback });
  };

  const closeNotify = () => {
    const callback = notify.onCloseCallback;
    setNotify({ isOpen: false, title: "", message: "", status: "info", onCloseCallback: null });
    if (callback) {
      callback();
    }
  };

  const closeDialog = () => setDialog({ isOpen: false, type: null });

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const approvalStatus = adjustment?.ApprovalStatus || "Pending";
  const isPending = approvalStatus === "Pending";
  const isArchived = approvalStatus === "Archived";
  const isRequester = Boolean(adjustment?.RequesterID) && adjustment.RequesterID === currentUserID;
  const wouldSelfApprove = isRequester;

  // เอกสารที่ยังรออนุมัติเท่านั้นที่แก้ไขได้ ผู้ดูแลระบบเริ่มที่โหมดตรวจสอบ
  // แล้วกดเปลี่ยนเป็นโหมดแก้ไขได้เมื่อจำเป็น เพื่อไม่ให้เผลอแก้คำขอของคลังอื่น
  const canEditLines = isNew || (isPending && (editMode || !isInventoryAdmin));
  const canApprove = !isNew && isPending && isInventoryAdmin;
  const canCancel = !isNew && isPending && (isInventoryAdmin || isRequester);
  const canAttach = !isNew && isPending;

  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineAdjustmentWarehouses",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouses(response.VaccineWarehousesList || []);
        setIsInventoryAdmin(Boolean(response.IsInventoryAdmin));
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  }, [sessionId]);

  const fetchVaccineOptions = useCallback(async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
      }
    } catch (error) {
      console.error("Error fetching vaccines:", error);
    }
  }, [sessionId]);

  const fetchInventory = useCallback(
    async (warehouseID, budgetType) => {
      if (!warehouseID || !budgetType) {
        setInventory([]);
        return;
      }
      setIsInventoryLoading(true);
      try {
        const response = await apiService({
          method: "POST",
          url: "/getVaccineInventoryByLot",
          data: { WarehouseID: warehouseID, BudgetType: budgetType },
          sessionId: sessionId || "",
        });
        setInventory(response.result === "success" ? response.InventoryReport || [] : []);
      } catch (error) {
        console.error("Error fetching inventory:", error);
        setInventory([]);
      } finally {
        setIsInventoryLoading(false);
      }
    },
    [sessionId]
  );

  const fetchAdjustment = useCallback(async () => {
    setIsLoading(true);
    setLoadError("");
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineAdjustmentsByID",
        data: { TransactionID: transactionId },
        sessionId: sessionId || "",
      });

      if (response.result !== "success") {
        setLoadError(response.error || "ไม่พบเอกสาร");
        return;
      }

      const loaded = response.VaccineAdjustment;
      const details = response.VaccineDetails || [];

      setAdjustment(loaded);
      setSavedDetails(details);
      setAttachedFiles(response.VaccineAdjustmentFiles || []);
      setIsInventoryAdmin(Boolean(response.IsInventoryAdmin));
      setCurrentUserID(response.CurrentUserID || "");

      const isPresetReason = requestTypeOptions.includes(loaded.RequestType);
      setHeader({
        RequestDate: loaded.RequestDate ? new Date(loaded.RequestDate) : new Date(),
        WarehouseID: loaded.WarehouseID || "",
        BudgetType: loaded.BudgetType || "Distribution",
        RequestType: isPresetReason ? loaded.RequestType : "อื่น ๆ",
        RequestTypeOther: isPresetReason ? "" : loaded.RequestType || "",
        ReferenceDocumentType: loaded.ReferenceDocumentType || "",
        ReferenceDocumentNo: loaded.ReferenceDocumentNo || "",
        WarehouseStaffID: loaded.WarehouseStaffID || "",
        RequestDetail: loaded.RequestDetail || "",
        Remark: loaded.Remark || "",
      });

      setLines(
        details.map((detail) => ({
          VaccineID: detail.VaccineID,
          LotID: detail.LotID,
          RequestQuantity: detail.RequestQuantity,
          Remark: detail.Remark || "",
          SystemQuantity: detail.CurrentQuantity ?? detail.PreviousQuantity,
        }))
      );

      setConfirmedQuantities(
        details.reduce((map, detail) => {
          map[detail.DetailID] = detail.RequestQuantity;
          return map;
        }, {})
      );
    } catch (error) {
      console.error("Error fetching adjustment:", error);
      setLoadError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, transactionId]);

  useEffect(() => {
    if (!sessionId) {
      return;
    }
    const load = async () => {
      await Promise.all([fetchWarehouses(), fetchVaccineOptions()]);
      if (transactionId) {
        await fetchAdjustment();
      }
    };
    load();
  }, [sessionId, transactionId, fetchWarehouses, fetchVaccineOptions, fetchAdjustment]);

  useEffect(() => {
    fetchInventory(header.WarehouseID, header.BudgetType);
  }, [header.WarehouseID, header.BudgetType, fetchInventory]);

  const inventoryByKey = useMemo(() => {
    const map = new Map();
    for (const item of inventory) {
      map.set(`${item.VaccineID}|${item.LotID}`, item);
    }
    return map;
  }, [inventory]);

  const vaccinesInWarehouse = useMemo(() => {
    const map = new Map();
    for (const item of inventory) {
      if (!map.has(item.VaccineID)) {
        map.set(item.VaccineID, item.VaccineNameTH);
      }
    }
    return Array.from(map, ([VaccineID, VaccineNameTH]) => ({ VaccineID, VaccineNameTH }));
  }, [inventory]);

  const lotsForSelectedVaccine = useMemo(
    () => inventory.filter((item) => item.VaccineID === newLine.VaccineID),
    [inventory, newLine.VaccineID]
  );

  const vaccineNameOf = useCallback(
    (vaccineID) => {
      const fromCatalog = vaccineOptions.find((item) => item.VaccineID === vaccineID);
      if (fromCatalog) {
        return fromCatalog.VaccineNameTH;
      }
      const fromInventory = inventory.find((item) => item.VaccineID === vaccineID);
      return fromInventory ? fromInventory.VaccineNameTH : vaccineID;
    },
    [vaccineOptions, inventory]
  );

  const systemQuantityOf = useCallback(
    (line) => {
      const found = inventoryByKey.get(`${line.VaccineID}|${line.LotID}`);
      if (found) {
        return Number(found.TotalQuantity || 0);
      }
      return Number(line.SystemQuantity || 0);
    },
    [inventoryByKey]
  );

  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    setHeader((prev) => ({ ...prev, [name]: value }));
  };

  const handleRequestDateChange = (e) => {
    const { value } = e.target;
    setHeader((prev) => ({ ...prev, RequestDate: value ? new Date(value) : null }));
  };

  const handleNewLineChange = (e) => {
    const { name, value } = e.target;
    setNewLine((prev) => ({
      ...prev,
      [name]: value,
      // เปลี่ยนวัคซีนแล้ว Lot เดิมย่อมไม่ใช่ของวัคซีนใหม่
      ...(name === "VaccineID" ? { LotID: "" } : {}),
    }));
  };

  const handleAddLine = () => {
    const vaccineID = newLine.VaccineID.trim();
    const lotID = newLine.LotID.trim();
    const quantity = parseInt(newLine.RequestQuantity, 10);

    if (!vaccineID || !lotID) {
      showNotify("ข้อมูลไม่ครบ", "กรุณาเลือกวัคซีนและ Lot ที่ต้องการปรับยอด", "warning");
      return;
    }

    if (!Number.isInteger(quantity) || quantity < 0) {
      showNotify("จำนวนไม่ถูกต้อง", "จำนวนที่นับได้ต้องเป็นจำนวนเต็มตั้งแต่ 0 ขึ้นไป", "warning");
      return;
    }

    if (lines.some((line) => line.VaccineID === vaccineID && line.LotID === lotID)) {
      showNotify("รายการซ้ำ", "Lot นี้อยู่ในรายการแล้ว กรุณาแก้ไขที่บรรทัดเดิมแทน", "warning");
      return;
    }

    const found = inventoryByKey.get(`${vaccineID}|${lotID}`);
    setLines((prev) => [
      ...prev,
      {
        VaccineID: vaccineID,
        LotID: lotID,
        RequestQuantity: quantity,
        Remark: newLine.Remark || "",
        SystemQuantity: found ? Number(found.TotalQuantity || 0) : 0,
      },
    ]);
    setNewLine({ VaccineID: "", LotID: "", RequestQuantity: "", Remark: "" });
  };

  const handleLineQuantityChange = (index, value) => {
    setLines((prev) =>
      prev.map((line, position) =>
        position === index ? { ...line, RequestQuantity: value === "" ? "" : parseInt(value, 10) } : line
      )
    );
  };

  const handleRemoveLine = (index) => {
    setLines((prev) => prev.filter((_, position) => position !== index));
  };

  const resolvedRequestType =
    header.RequestType === "อื่น ๆ" ? header.RequestTypeOther.trim() : header.RequestType;

  const linesWithDifference = useMemo(
    () =>
      lines.map((line) => {
        const systemQuantity = systemQuantityOf(line);
        const counted = line.RequestQuantity === "" ? 0 : Number(line.RequestQuantity);
        return {
          ...line,
          SystemQuantity: systemQuantity,
          Difference: counted - systemQuantity,
          HoldQuantity: Math.max(0, systemQuantity - counted),
        };
      }),
    [lines, systemQuantityOf]
  );

  const hasDifference = linesWithDifference.some((line) => line.Difference !== 0);

  const buildDetailPayload = () =>
    lines.map((line) => ({
      VaccineID: line.VaccineID,
      LotID: line.LotID,
      RequestQuantity: line.RequestQuantity === "" ? 0 : Number(line.RequestQuantity),
      Remark: line.Remark || "",
    }));

  const validateBeforeSave = () => {
    if (!header.WarehouseID) {
      showNotify("ข้อมูลไม่ครบ", "กรุณาเลือกคลังที่ขอปรับยอด", "warning");
      return false;
    }
    if (!resolvedRequestType) {
      showNotify("ข้อมูลไม่ครบ", "กรุณาระบุเหตุผลของการขอปรับยอด", "warning");
      return false;
    }
    if (lines.length === 0) {
      showNotify("ข้อมูลไม่ครบ", "กรุณาเพิ่มรายการวัคซีนที่ขอปรับยอดอย่างน้อยหนึ่งรายการ", "warning");
      return false;
    }
    if (lines.some((line) => line.RequestQuantity === "" || Number(line.RequestQuantity) < 0)) {
      showNotify("จำนวนไม่ถูกต้อง", "กรุณากรอกจำนวนที่นับได้ให้ครบทุกรายการ", "warning");
      return false;
    }
    if (!hasDifference) {
      showNotify(
        "ไม่ต้องปรับยอด",
        "ทุกรายการมีจำนวนที่นับได้ตรงกับยอดในระบบอยู่แล้ว จึงไม่จำเป็นต้องยื่นคำขอ",
        "info"
      );
      return false;
    }
    // การปรับยอดไม่มีเอกสารต้นทางอย่างใบรับผลิตหรือใบเบิกมารองรับ
    // จึงต้องมีหลักฐานการตรวจนับหรือหนังสืออนุมัติแนบมาตั้งแต่ยื่น
    if (isNew && pendingFiles.length === 0) {
      showNotify(
        "ยังไม่มีเอกสารประกอบ",
        "กรุณาแนบเอกสารประกอบอย่างน้อยหนึ่งไฟล์ เช่น ใบตรวจนับ หรือหนังสืออนุมัติ",
        "warning"
      );
      return false;
    }
    return true;
  };

  const handleSubmitRequest = async () => {
    closeDialog();
    setIsSaving(true);
    try {
      // ส่งเอกสารประกอบไปพร้อมคำขอในคำขอเดียว เพื่อให้ใบที่บันทึกสำเร็จมีหลักฐานครบเสมอ
      const formData = new FormData();
      formData.append("RequestDate", header.RequestDate || "");
      formData.append("WarehouseID", header.WarehouseID || "");
      formData.append("BudgetType", header.BudgetType || "");
      formData.append("RequestType", resolvedRequestType || "");
      formData.append("ReferenceDocumentType", header.ReferenceDocumentType || "");
      formData.append("ReferenceDocumentNo", header.ReferenceDocumentNo || "");
      formData.append("WarehouseStaffID", header.WarehouseStaffID || "");
      formData.append("RequestDetail", header.RequestDetail || "");
      formData.append("Remark", header.Remark || "");
      formData.append("DocumentType", documentType || "");
      formData.append("VaccineDetails", JSON.stringify(buildDetailPayload()));
      pendingFiles.forEach((file) => formData.append("EvidenceFiles", file));

      const response = await apiService({
        method: "POST",
        url: "/createVaccineAdjustments",
        data: formData,
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        const held = (response.HeldItems || []).reduce(
          (sum, item) => sum + Number(item.HoldQuantity || 0),
          0
        );
        showNotify(
          "ยื่นคำขอสำเร็จ",
          `เลขที่เอกสาร ${response.TransactionID}` +
            (held > 0
              ? ` ระบบกันยอดไว้ ${formatNumber(held)} ขวด จนกว่าผู้ดูแลระบบจะยืนยัน`
              : ""),
          "success",
          () => navigate(`/vaccineadjustment/${response.TransactionID}`)
        );
      }
    } catch (error) {
      showNotify("ยื่นคำขอไม่สำเร็จ", error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveChanges = async () => {
    closeDialog();
    setIsSaving(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateVaccineAdjustmentsByID",
        data: {
          TransactionID: transactionId,
          RequestDate: header.RequestDate,
          BudgetType: header.BudgetType,
          RequestType: resolvedRequestType,
          ReferenceDocumentType: header.ReferenceDocumentType,
          ReferenceDocumentNo: header.ReferenceDocumentNo,
          WarehouseStaffID: header.WarehouseStaffID,
          RequestDetail: header.RequestDetail,
          Remark: header.Remark,
          VaccineDetails: buildDetailPayload(),
        },
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        setEditMode(false);
        await fetchAdjustment();
        showNotify("บันทึกสำเร็จ", "ปรับปรุงคำขอและการกันยอดเรียบร้อยแล้ว", "success");
      }
    } catch (error) {
      showNotify("บันทึกไม่สำเร็จ", error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleApprove = async () => {
    closeDialog();
    setIsSaving(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/approveVaccineAdjustmentsByID",
        data: {
          TransactionID: transactionId,
          Remark: header.Remark,
          VaccineDetails: savedDetails.map((detail) => ({
            DetailID: detail.DetailID,
            ConfirmedQuantity:
              confirmedQuantities[detail.DetailID] === "" ||
              confirmedQuantities[detail.DetailID] === undefined
                ? detail.RequestQuantity
                : Number(confirmedQuantities[detail.DetailID]),
          })),
        },
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        const changed = (response.AppliedDetails || []).filter((item) => item.ChangeQuantity !== 0);
        showNotify(
          "ยืนยันการปรับยอดสำเร็จ",
          changed.length > 0
            ? `ปรับยอดคงคลังแล้ว ${changed.length} รายการ`
            : "บันทึกผลการตรวจสอบแล้ว โดยไม่มีรายการใดต้องปรับยอด",
          "success",
          () => navigate("/vaccineadjustmentlist")
        );
      }
    } catch (error) {
      showNotify("ยืนยันไม่สำเร็จ", error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseWithoutApplying = async (action) => {
    closeDialog();
    setIsSaving(true);
    const url =
      action === "reject" ? "/rejectVaccineAdjustmentsByID" : "/cancelVaccineAdjustmentsByID";
    try {
      const response = await apiService({
        method: "POST",
        url,
        data: { TransactionID: transactionId, Remark: header.Remark },
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        showNotify(
          action === "reject" ? "ไม่อนุมัติคำขอแล้ว" : "ยกเลิกคำขอแล้ว",
          "ยอดคงคลังไม่ถูกแก้ไข และการกันยอดถูกปลดเรียบร้อยแล้ว",
          "success",
          () => navigate("/vaccineadjustmentlist")
        );
      }
    } catch (error) {
      showNotify("ดำเนินการไม่สำเร็จ", error.message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    setPendingFiles((prev) => [...prev, ...acceptedFiles].slice(0, 5));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 5,
    maxSize: 10 * 1024 * 1024,
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "application/pdf": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
    },
  });

  const handleUploadFiles = async () => {
    if (pendingFiles.length === 0) {
      showNotify("ยังไม่ได้เลือกไฟล์", "กรุณาเลือกไฟล์เอกสารประกอบก่อน", "warning");
      return;
    }
    setIsUploading(true);
    try {
      const payload = new FormData();
      payload.append("TransactionID", transactionId);
      payload.append("DocumentType", documentType || "");
      pendingFiles.forEach((file) => payload.append("EvidenceFiles", file));

      const response = await apiService({
        method: "POST",
        url: "/uploadVaccineAdjustmentFiles",
        data: payload,
        sessionId: sessionId || "",
      });

      if (response.result === "success") {
        setPendingFiles([]);
        await fetchAdjustment();
        showNotify("แนบเอกสารสำเร็จ", "ระบบบันทึกเอกสารประกอบเรียบร้อยแล้ว", "success");
      }
    } catch (error) {
      showNotify("แนบเอกสารไม่สำเร็จ", error.message, "error");
    } finally {
      setIsUploading(false);
    }
  };

  if (!sessionId) {
    return null;
  }

  // ตอนยื่นคำขอสำเร็จแล้วเปลี่ยนหน้าไปที่เลขที่เอกสารใหม่ จะมีจังหวะที่ transactionId
  // มาถึงแล้วแต่ยังไม่ได้เรียก API จึงยังไม่มีข้อมูลเอกสาร ต้องรอให้โหลดเสร็จก่อน
  // ไม่งั้นส่วนที่อ่านค่าจากเอกสารจะพังเพราะข้อมูลยังเป็น null
  if (isLoading || (!isNew && !adjustment && !loadError)) {
    return (
      <Box p={{ base: 6, md: 10 }} m={{ base: 2, md: 4 }} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box p={{ base: 3, sm: 4, md: 6 }} m={{ base: 2, md: 4 }}>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>เปิดเอกสารไม่ได้</AlertTitle>
            <AlertDescription>{loadError}</AlertDescription>
          </Box>
        </Alert>
        <Box mt={5}>
          <IconButton.Back onClick={() => navigate("/vaccineadjustmentlist")} label="ย้อนกลับ" />
        </Box>
      </Box>
    );
  }

  const statusInfo = approvalStatusInfo[approvalStatus] || { label: approvalStatus, scheme: "gray" };

  return (
    <Box
      p={{ base: 3, sm: 4, md: 6 }}
      m={{ base: 2, md: 4 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <Flex
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={3}
        mb={{ base: 4, md: 5 }}
      >
        <Heading as="h2" size={{ base: "sm", md: "md" }}>
          {isNew ? "ยื่นคำขอปรับยอดวัคซีนคงคลัง" : "คำขอปรับยอดวัคซีนคงคลัง"}
        </Heading>
        <HStack spacing={3} flexWrap="wrap">
          {!isNew && (
            <Badge colorScheme={statusInfo.scheme} borderRadius="full" px={4} py={1} fontSize="sm">
              {statusInfo.label}
            </Badge>
          )}
          {isInventoryAdmin && (
            <Badge colorScheme="purple" borderRadius="full" px={3} py={1}>
              <HStack spacing={2}>
                <FontAwesomeIcon icon={faShieldHalved} />
                <span>ผู้ดูแลระบบ</span>
              </HStack>
            </Badge>
          )}
        </HStack>
      </Flex>

      {isArchived && (
        <Alert status="warning" borderRadius="lg" mb={6}>
          <AlertIcon />
          <Box>
            <AlertTitle>เอกสารก่อนเปิดใช้กระบวนการปรับยอด</AlertTitle>
            <AlertDescription>
              เอกสารใบนี้ค้างอยู่ในระบบตั้งแต่ก่อนที่การปรับยอดจะเชื่อมกับคลังจริง
              จึงเปิดอ่านได้อย่างเดียว หากยังต้องปรับยอด กรุณาตรวจนับใหม่แล้วยื่นคำขอใบใหม่
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {!isNew && isPending && (
        <Alert status="info" borderRadius="lg" mb={6}>
          <AlertIcon />
          <Box>
            <AlertTitle>ระบบกันยอดส่วนที่ขอลดไว้แล้ว</AlertTitle>
            <AlertDescription>
              คลังยังเบิกจ่ายหรือรับเข้าส่วนที่เหลือได้ตามปกติ
              แต่จะจ่ายจนต่ำกว่าจำนวนที่นับได้ไม่ได้ จนกว่าคำขอนี้จะได้ข้อสรุป
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {!isNew && approvalStatus === "Approved" && (
        <Alert status="success" borderRadius="lg" mb={6}>
          <AlertIcon />
          <Box>
            <AlertTitle>ปรับยอดคงคลังเรียบร้อยแล้ว</AlertTitle>
            <AlertDescription>
              ยืนยันโดย {adjustment.ApproverID} เมื่อ {formatDateTime(adjustment.ApprovalDate)}
              {adjustment.SelfApproved ? " โดยเป็นผู้ยื่นและผู้ยืนยันคนเดียวกัน" : ""}
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* ---------- ข้อมูลเอกสาร ---------- */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={{ base: 3, md: 4 }} mb={{ base: 4, md: 5 }}>
        <StandardHeader
          title="ข้อมูลใบปรับยอดวัคซีน"
          bgColor="iBlue.600"
          icon={faFileLines}
          size="sm"
        />

        <Grid
          templateColumns={{ base: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }}
          gap={{ base: 3, md: 4 }}
          mt={4}
        >
          {!isNew && (
            <GridItem colSpan={1}>
              <FormControl>
                <FormLabel color="gray">เลขที่เอกสาร</FormLabel>
                <Input value={adjustment.TransactionID} isReadOnly bg="gray.50" />
              </FormControl>
            </GridItem>
          )}

          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel color="gray">วันที่ตรวจนับ</FormLabel>
              <CustomDatePicker
                name="RequestDate"
                value={header.RequestDate}
                onChange={handleRequestDateChange}
                isDisabled={!canEditLines}
              />
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 1, sm: 2, lg: 2 }}>
            <FormControl isRequired>
              <FormLabel color="gray">คลัง</FormLabel>
              <Select
                name="WarehouseID"
                value={header.WarehouseID}
                onChange={handleHeaderChange}
                placeholder="เลือกคลัง"
                isDisabled={!isNew}
              >
                {warehouses.map((warehouse) => (
                  <option key={warehouse.WarehouseID} value={warehouse.WarehouseID}>
                    {warehouse.WarehouseName}
                  </option>
                ))}
              </Select>
              {isNew && (
                <FormHelperText>
                  ผู้ดูแลระบบเลือกคลังใดก็ได้ เจ้าหน้าที่คลังจะเห็นเฉพาะคลังในสังกัด
                </FormHelperText>
              )}
            </FormControl>
          </GridItem>

          <GridItem colSpan={1}>
            <FormControl isRequired>
              <FormLabel color="gray">ประเภทงบประมาณ</FormLabel>
              <Select
                name="BudgetType"
                value={header.BudgetType}
                onChange={handleHeaderChange}
                isDisabled={!canEditLines}
              >
                {budgetTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 1, sm: 2, lg: 2 }}>
            <FormControl isRequired>
              <FormLabel color="gray">เหตุผลที่ขอปรับยอด</FormLabel>
              <Select
                name="RequestType"
                value={header.RequestType}
                onChange={handleHeaderChange}
                placeholder="เลือกเหตุผล"
                isDisabled={!canEditLines}
              >
                {requestTypeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            </FormControl>
          </GridItem>

          {header.RequestType === "อื่น ๆ" && (
            <GridItem colSpan={{ base: 1, sm: 2, lg: 2 }}>
              <FormControl isRequired>
                <FormLabel color="gray">ระบุเหตุผล</FormLabel>
                <Input
                  name="RequestTypeOther"
                  value={header.RequestTypeOther}
                  onChange={handleHeaderChange}
                  isReadOnly={!canEditLines}
                />
              </FormControl>
            </GridItem>
          )}

          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel color="gray">ประเภทเอกสารอ้างอิง</FormLabel>
              <Input
                name="ReferenceDocumentType"
                value={header.ReferenceDocumentType}
                onChange={handleHeaderChange}
                placeholder="เช่น หนังสืออนุมัติ"
                isReadOnly={!canEditLines}
              />
            </FormControl>
          </GridItem>

          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel color="gray">เลขที่เอกสารอ้างอิง</FormLabel>
              <Input
                name="ReferenceDocumentNo"
                value={header.ReferenceDocumentNo}
                onChange={handleHeaderChange}
                isReadOnly={!canEditLines}
              />
            </FormControl>
          </GridItem>

          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel color="gray">เจ้าหน้าที่ผู้ตรวจนับ</FormLabel>
              <Input
                name="WarehouseStaffID"
                value={header.WarehouseStaffID}
                onChange={handleHeaderChange}
                isReadOnly={!canEditLines}
              />
            </FormControl>
          </GridItem>

          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel color="gray">ผู้ยื่นคำขอ</FormLabel>
              <Input
                value={isNew ? profile?.Email || profile?.UserID || "" : adjustment.RequesterID || "-"}
                isReadOnly
                bg="gray.50"
              />
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 1, sm: 2, lg: 4 }}>
            <FormControl>
              <FormLabel color="gray">รายละเอียดการตรวจนับ</FormLabel>
              <Textarea
                name="RequestDetail"
                value={header.RequestDetail}
                onChange={handleHeaderChange}
                isReadOnly={!canEditLines}
                placeholder="อธิบายที่มาของส่วนต่าง เช่น ผลการตรวจนับคงคลังประจำปี พร้อมเลขที่คำสั่งแต่งตั้งคณะกรรมการ"
              />
            </FormControl>
          </GridItem>

          <GridItem colSpan={{ base: 1, sm: 2, lg: 4 }}>
            <FormControl>
              <FormLabel color="gray">หมายเหตุ</FormLabel>
              <Textarea
                name="Remark"
                value={header.Remark}
                onChange={handleHeaderChange}
                isReadOnly={!isNew && !isPending}
              />
            </FormControl>
          </GridItem>
        </Grid>
      </Box>

      {/* ---------- เพิ่มรายการ ---------- */}
      {canEditLines && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={{ base: 3, md: 4 }} mb={{ base: 4, md: 5 }}>
          <StandardHeader
            title="เพิ่มรายการที่ตรวจนับได้"
            bgColor="iPurple.500"
            icon={faSyringe}
            size="sm"
          />

          {!header.WarehouseID ? (
            <Text color="gray.500" mt={4}>
              กรุณาเลือกคลังก่อน ระบบจะแสดง Lot ที่มีอยู่ในคลังนั้นให้เลือก
            </Text>
          ) : (
            <Box mt={4}>
              <Checkbox
                mb={4}
                isChecked={manualLot}
                onChange={(e) => {
                  setManualLot(e.target.checked);
                  setNewLine({ VaccineID: "", LotID: "", RequestQuantity: "", Remark: "" });
                }}
              >
                ระบุ Lot เอง กรณีตรวจนับพบวัคซีนที่ระบบยังไม่มียอดในคลังนี้
              </Checkbox>

              <Grid
                templateColumns={{
                  base: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "2fr 2fr 1fr auto",
                }}
                gap={{ base: 3, md: 4 }}
                alignItems="end"
              >
                <FormControl>
                  <FormLabel color="gray">วัคซีน</FormLabel>
                  <Select
                    name="VaccineID"
                    value={newLine.VaccineID}
                    onChange={handleNewLineChange}
                    placeholder={isInventoryLoading ? "กำลังโหลด..." : "เลือกวัคซีน"}
                  >
                    {(manualLot ? vaccineOptions : vaccinesInWarehouse).map((vaccine) => (
                      <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                        {vaccine.VaccineNameTH}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel color="gray">Lot No.</FormLabel>
                  {manualLot ? (
                    <Input
                      name="LotID"
                      value={newLine.LotID}
                      onChange={handleNewLineChange}
                      placeholder="ระบุ Lot ตามที่พิมพ์บนขวด"
                    />
                  ) : (
                    <Select
                      name="LotID"
                      value={newLine.LotID}
                      onChange={handleNewLineChange}
                      placeholder={newLine.VaccineID ? "เลือก Lot" : "เลือกวัคซีนก่อน"}
                      isDisabled={!newLine.VaccineID}
                    >
                      {lotsForSelectedVaccine.map((item) => (
                        <option key={item.LotID} value={item.LotID}>
                          {`${item.LotID} (ระบบมี ${formatNumber(item.TotalQuantity)} ขวด)`}
                        </option>
                      ))}
                    </Select>
                  )}
                </FormControl>

                <FormControl>
                  <FormLabel color="gray">จำนวนที่นับได้</FormLabel>
                  <Input
                    name="RequestQuantity"
                    type="number"
                    min={0}
                    value={newLine.RequestQuantity}
                    onChange={handleNewLineChange}
                  />
                </FormControl>

                <Box pb={1} gridColumn={{ base: "auto", sm: "1 / -1", lg: "auto" }}>
                  <IconButton.Add onClick={handleAddLine} label="เพิ่ม" />
                </Box>
              </Grid>

              {!manualLot && newLine.VaccineID && lotsForSelectedVaccine.length === 0 && (
                <Text color="orange.600" fontSize="sm" mt={3}>
                  ไม่พบ Lot ของวัคซีนนี้ในคลังและงบประมาณที่เลือก
                  หากตรวจนับพบของจริง ให้ติ๊กระบุ Lot เอง
                </Text>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* ---------- ตารางรายการ ---------- */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={{ base: 3, md: 4 }} mb={{ base: 4, md: 5 }}>
        <StandardHeader
          title="รายการวัคซีนที่ขอปรับยอด"
          bgColor="iOrange.500"
          icon={faTable}
          size="sm"
        />

        {canEditLines ? (
          <TableContainer mt={4} overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>วัคซีน</Th>
                  <Th>Lot</Th>
                  <Th isNumeric>ยอดในระบบ</Th>
                  <Th isNumeric>จำนวนที่นับได้</Th>
                  <Th isNumeric>ผลต่าง</Th>
                  <Th isNumeric>จะกันยอดไว้</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {linesWithDifference.length === 0 ? (
                  <Tr>
                    <Td colSpan={7}>
                      <Text color="gray.500" textAlign="center" py={4}>
                        ยังไม่มีรายการ
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  linesWithDifference.map((line, index) => (
                    <Tr key={`${line.VaccineID}-${line.LotID}`}>
                      <Td>
                        <Text fontWeight="semibold">{vaccineNameOf(line.VaccineID)}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {line.VaccineID}
                        </Text>
                      </Td>
                      <Td>{line.LotID}</Td>
                      <Td isNumeric>
                        <Text color={line.SystemQuantity < 0 ? "red.600" : undefined}>
                          {formatNumber(line.SystemQuantity)}
                        </Text>
                      </Td>
                      <Td isNumeric>
                        <Input
                          type="number"
                          min={0}
                          size="sm"
                          width="120px"
                          textAlign="right"
                          value={line.RequestQuantity}
                          onChange={(e) => handleLineQuantityChange(index, e.target.value)}
                        />
                      </Td>
                      <Td isNumeric>
                        <DifferenceTag difference={line.Difference} />
                      </Td>
                      <Td isNumeric>{formatNumber(line.HoldQuantity)}</Td>
                      <Td>
                        <IconButton.DeleteIcon onClick={() => handleRemoveLine(index)} size="sm" />
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>
        ) : (
          <TableContainer mt={4} overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>วัคซีน</Th>
                  <Th>Lot</Th>
                  <Th isNumeric>ยอดตอนยื่นคำขอ</Th>
                  <Th isNumeric>จำนวนที่นับได้</Th>
                  <Th isNumeric>ยอดในระบบตอนนี้</Th>
                  <Th isNumeric>กันไว้</Th>
                  {canApprove && <Th isNumeric>จำนวนที่ยืนยัน</Th>}
                  <Th isNumeric>ผลต่างที่จะปรับ</Th>
                </Tr>
              </Thead>
              <Tbody>
                {savedDetails.map((detail) => {
                  const confirmed =
                    confirmedQuantities[detail.DetailID] === "" ||
                    confirmedQuantities[detail.DetailID] === undefined
                      ? detail.RequestQuantity
                      : Number(confirmedQuantities[detail.DetailID]);
                  const change = confirmed - detail.PreviousQuantity;
                  const drifted =
                    detail.CurrentQuantity !== undefined &&
                    detail.CurrentQuantity !== detail.PreviousQuantity;

                  return (
                    <Tr key={detail.DetailID}>
                      <Td>
                        <Text fontWeight="semibold">{vaccineNameOf(detail.VaccineID)}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {detail.VaccineID}
                        </Text>
                      </Td>
                      <Td>{detail.LotID}</Td>
                      <Td isNumeric>{formatNumber(detail.PreviousQuantity)}</Td>
                      <Td isNumeric>{formatNumber(detail.RequestQuantity)}</Td>
                      <Td isNumeric>
                        <Text color={drifted ? "orange.600" : undefined} fontWeight={drifted ? "semibold" : "normal"}>
                          {formatNumber(detail.CurrentQuantity)}
                        </Text>
                      </Td>
                      <Td isNumeric>{formatNumber(detail.HoldQuantity)}</Td>
                      {canApprove && (
                        <Td isNumeric>
                          <Input
                            type="number"
                            min={0}
                            size="sm"
                            width="120px"
                            textAlign="right"
                            value={confirmedQuantities[detail.DetailID] ?? detail.RequestQuantity}
                            onChange={(e) =>
                              setConfirmedQuantities((prev) => ({
                                ...prev,
                                [detail.DetailID]:
                                  e.target.value === "" ? "" : parseInt(e.target.value, 10),
                              }))
                            }
                          />
                        </Td>
                      )}
                      <Td isNumeric>
                        <DifferenceTag difference={change} />
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}

        {!canEditLines &&
          savedDetails.some(
            (detail) =>
              detail.CurrentQuantity !== undefined &&
              detail.CurrentQuantity !== detail.PreviousQuantity
          ) && (
          <Alert status="warning" borderRadius="lg" mt={4}>
            <AlertIcon />
            <AlertDescription>
              ยอดในระบบเปลี่ยนไปหลังจากยื่นคำขอ ระบบจะปรับด้วยผลต่างที่ตรวจนับได้
              ไม่ใช่การตั้งยอดทับ การเบิกจ่ายที่เกิดขึ้นระหว่างรออนุมัติจึงยังอยู่ครบ
            </AlertDescription>
          </Alert>
        )}
      </Box>

      {/* ---------- เอกสารประกอบ ---------- */}
      {(isNew || canAttach || attachedFiles.length > 0) && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={{ base: 3, md: 4 }} mb={{ base: 4, md: 5 }}>
          <StandardHeader
            title="เอกสารประกอบ"
            bgColor="iGreen.500"
            icon={faPaperclip}
            size="sm"
          />

          <Box mt={4}>
            {isNew && (
              <Alert status="info" borderRadius="lg" mb={4}>
                <AlertIcon />
                <AlertDescription>
                  การปรับยอดไม่มีเอกสารต้นทางอย่างใบรับผลิตหรือใบเบิกมารองรับ
                  จึงต้องแนบหลักฐานอย่างน้อยหนึ่งไฟล์ เช่น ใบตรวจนับ หรือหนังสืออนุมัติ
                  ไฟล์จะถูกส่งไปพร้อมกับการยื่นคำขอ
                </AlertDescription>
              </Alert>
            )}

            {isNew ? null : attachedFiles.length === 0 ? (
              <Text color="gray.500" mb={4}>
                ยังไม่มีเอกสารแนบ
              </Text>
            ) : (
              <VStack align="stretch" spacing={2} mb={4}>
                {attachedFiles.map((file) => (
                  <Flex
                    key={file.FileID}
                    justify="space-between"
                    align={{ base: "flex-start", sm: "center" }}
                    direction={{ base: "column", sm: "row" }}
                    gap={2}
                    borderWidth={1}
                    borderRadius="md"
                    p={3}
                  >
                    <Box minW={0}>
                      <Text fontWeight="semibold" wordBreak="break-word">
                        {file.FileName}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {file.DocumentType || "ไม่ระบุประเภท"} · {formatFileSize(file.FileSize)} ·
                        แนบโดย {file.CreateBy} เมื่อ {formatDateTime(file.CreatedAt)}
                      </Text>
                    </Box>
                    {file.FileUrl && (
                      <Link
                        href={`${BASE_URL}${file.FileUrl}`}
                        isExternal
                        color="iGreen.600"
                        fontWeight="semibold"
                        flexShrink={0}
                      >
                        เปิดดู
                      </Link>
                    )}
                  </Flex>
                ))}
              </VStack>
            )}

            {(isNew || canAttach) && (
              <>
                {!isNew && <Divider my={4} />}
                <FormControl mb={3} maxW="sm">
                  <FormLabel color="gray">ประเภทเอกสาร</FormLabel>
                  <Input value={documentType} onChange={(e) => setDocumentType(e.target.value)} />
                </FormControl>

                <Box
                  {...getRootProps()}
                  borderWidth={2}
                  borderStyle="dashed"
                  borderColor={isDragActive ? "green.400" : "gray.300"}
                  borderRadius="lg"
                  p={{ base: 4, md: 6 }}
                  textAlign="center"
                  cursor="pointer"
                  bg={isDragActive ? "green.50" : "gray.50"}
                >
                  <input {...getInputProps()} />
                  <Text color="gray.600" fontSize={{ base: "sm", md: "md" }}>
                    {isDragActive
                      ? "ปล่อยไฟล์ที่นี่"
                      : "ลากไฟล์มาวาง หรือคลิกเพื่อเลือก รองรับ PDF รูปภาพ Word และ Excel ไม่เกินไฟล์ละ 10 MB ครั้งละไม่เกิน 5 ไฟล์"}
                  </Text>
                </Box>

                {pendingFiles.length > 0 && (
                  <VStack align="stretch" spacing={2} mt={4}>
                    {pendingFiles.map((file, index) => (
                      <Flex
                        key={`${file.name}-${index}`}
                        justify="space-between"
                        align="center"
                        gap={2}
                      >
                        <Text fontSize="sm" minW={0} wordBreak="break-word">
                          {file.name} ({formatFileSize(file.size)})
                        </Text>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          flexShrink={0}
                          onClick={() =>
                            setPendingFiles((prev) => prev.filter((_, position) => position !== index))
                          }
                        >
                          นำออก
                        </Button>
                      </Flex>
                    ))}
                    {/* ใบใหม่ส่งไฟล์ไปพร้อมปุ่มยื่นคำขอ จึงไม่มีปุ่มอัปโหลดแยก */}
                    {!isNew && (
                      <Box textAlign="right">
                        <IconButton.Upload
                          onClick={handleUploadFiles}
                          isLoading={isUploading}
                          label="อัปโหลดเอกสาร"
                        />
                      </Box>
                    )}
                  </VStack>
                )}

                {wouldSelfApprove && isInventoryAdmin && attachedFiles.length === 0 && (
                  <Alert status="warning" borderRadius="lg" mt={4}>
                    <AlertIcon />
                    <AlertDescription>
                      คุณเป็นผู้ยื่นคำขอใบนี้เอง หากจะยืนยันด้วยตนเอง
                      ระบบกำหนดให้แนบเอกสารอนุมัติไว้เป็นหลักฐานก่อน
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}
          </Box>
        </Box>
      )}

      {/* ---------- ปุ่มดำเนินการ ---------- */}
      <Box pt={{ base: 2, md: 3 }}>
        <Flex
          gap={3}
          justify={{ base: "flex-start", md: "flex-end" }}
          wrap="wrap"
          align="center"
        >
          <IconButton.Back onClick={() => navigate("/vaccineadjustmentlist")} label="ย้อนกลับ" />

          {isNew && (
            <IconButton.Submit
              label="ยื่นคำขอปรับยอด"
              onClick={() => {
                if (validateBeforeSave()) {
                  setDialog({ isOpen: true, type: "submit" });
                }
              }}
              isLoading={isSaving}
            />
          )}

          {!isNew && isPending && isInventoryAdmin && !editMode && (
            <IconButton.Edit label="แก้ไขคำขอ" onClick={() => setEditMode(true)} />
          )}

          {!isNew && isPending && canEditLines && (
            <IconButton.Save
              label="บันทึกการแก้ไข"
              onClick={() => {
                if (validateBeforeSave()) {
                  setDialog({ isOpen: true, type: "save" });
                }
              }}
              isLoading={isSaving}
            />
          )}

          {canCancel && (
            <IconButton.Cancel
              label="ยกเลิกคำขอ"
              onClick={() => setDialog({ isOpen: true, type: "cancel" })}
              isLoading={isSaving}
            />
          )}

          {canApprove && (
            <>
              <IconButton.Reject
                label="ไม่อนุมัติ"
                onClick={() => setDialog({ isOpen: true, type: "reject" })}
                isLoading={isSaving}
              />
              <IconButton.Approved
                label="ยืนยันการปรับยอด"
                onClick={() => setDialog({ isOpen: true, type: "approve" })}
                isLoading={isSaving}
              />
            </>
          )}

          {!isNew && !isPending && (
            <HStack color="gray.500" spacing={2}>
              <FontAwesomeIcon icon={faLock} />
              <Text fontSize="sm">เอกสารปิดแล้ว แก้ไขไม่ได้</Text>
            </HStack>
          )}
        </Flex>
      </Box>

      {/* ---------- กล่องยืนยัน ---------- */}
      <AlertDialog
        isOpen={dialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeDialog}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent maxW="lg">
            {dialog.type === "submit" && (
              <>
                <AlertDialogHeader fontSize="xl" fontWeight="bold" color="iBlue.700">
                  ยืนยันการยื่นคำขอปรับยอด
                </AlertDialogHeader>
                <AlertDialogBody py={5}>
                  <Text mb={3}>
                    ระบบจะบันทึกคำขอ {linesWithDifference.length} รายการ
                    และกันยอดส่วนที่ขอลดไว้ทันที จนกว่าผู้ดูแลระบบจะยืนยันหรือไม่อนุมัติ
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    ยอดคงคลังจะยังไม่ถูกแก้ไขในขั้นตอนนี้
                  </Text>
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeDialog}>
                    ยกเลิก
                  </Button>
                  <Button colorScheme="blue" ml={3} onClick={handleSubmitRequest} isLoading={isSaving}>
                    ยืนยันยื่นคำขอ
                  </Button>
                </AlertDialogFooter>
              </>
            )}

            {dialog.type === "save" && (
              <>
                <AlertDialogHeader fontSize="xl" fontWeight="bold">
                  บันทึกการแก้ไขคำขอ
                </AlertDialogHeader>
                <AlertDialogBody py={5}>
                  ระบบจะปลดการกันยอดเดิมแล้วกันยอดใหม่ตามจำนวนที่แก้ไข
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeDialog}>
                    ยกเลิก
                  </Button>
                  <Button colorScheme="blue" ml={3} onClick={handleSaveChanges} isLoading={isSaving}>
                    บันทึก
                  </Button>
                </AlertDialogFooter>
              </>
            )}

            {dialog.type === "approve" && (
              <>
                <AlertDialogHeader fontSize="xl" fontWeight="bold" color="green.600">
                  ยืนยันการปรับยอดคงคลัง
                </AlertDialogHeader>
                <AlertDialogBody py={5}>
                  <Text mb={3}>
                    ระบบจะปรับยอดคงคลังตามผลต่างที่ตรวจนับได้ และบันทึกประวัติการเคลื่อนไหวไว้
                    การปรับยอดนี้ย้อนกลับเองไม่ได้
                  </Text>
                  <VStack align="stretch" spacing={1} fontSize="sm">
                    {savedDetails.map((detail) => {
                      const confirmed =
                        confirmedQuantities[detail.DetailID] === "" ||
                        confirmedQuantities[detail.DetailID] === undefined
                          ? detail.RequestQuantity
                          : Number(confirmedQuantities[detail.DetailID]);
                      const change = confirmed - detail.PreviousQuantity;
                      return (
                        <HStack key={detail.DetailID} justify="space-between">
                          <Text>
                            {detail.VaccineID} Lot {detail.LotID}
                          </Text>
                          <DifferenceTag difference={change} />
                        </HStack>
                      );
                    })}
                  </VStack>
                  {wouldSelfApprove && (
                    <Alert status="warning" borderRadius="md" mt={4}>
                      <AlertIcon />
                      <AlertDescription fontSize="sm">
                        คุณเป็นทั้งผู้ยื่นและผู้ยืนยัน ระบบจะบันทึกไว้ว่าเป็นการทำรายการคนเดียวทั้งขา
                      </AlertDescription>
                    </Alert>
                  )}
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeDialog}>
                    ยกเลิก
                  </Button>
                  <Button colorScheme="green" ml={3} onClick={handleApprove} isLoading={isSaving}>
                    ยืนยันการปรับยอด
                  </Button>
                </AlertDialogFooter>
              </>
            )}

            {(dialog.type === "reject" || dialog.type === "cancel") && (
              <>
                <AlertDialogHeader fontSize="xl" fontWeight="bold" color="red.600">
                  {dialog.type === "reject" ? "ไม่อนุมัติคำขอปรับยอด" : "ยกเลิกคำขอปรับยอด"}
                </AlertDialogHeader>
                <AlertDialogBody py={5}>
                  <Text mb={3}>
                    ยอดคงคลังจะไม่ถูกแก้ไข และการกันยอดของคำขอนี้จะถูกปลดทันที
                  </Text>
                  <FormControl>
                    <FormLabel fontSize="sm">เหตุผล บันทึกลงในหมายเหตุของเอกสาร</FormLabel>
                    <Textarea
                      name="Remark"
                      value={header.Remark}
                      onChange={handleHeaderChange}
                      size="sm"
                    />
                  </FormControl>
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeDialog}>
                    ย้อนกลับ
                  </Button>
                  <Button
                    colorScheme="red"
                    ml={3}
                    onClick={() => handleCloseWithoutApplying(dialog.type)}
                    isLoading={isSaving}
                  >
                    {dialog.type === "reject" ? "ยืนยันไม่อนุมัติ" : "ยืนยันยกเลิก"}
                  </Button>
                </AlertDialogFooter>
              </>
            )}
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* ---------- กล่องแจ้งผล ---------- */}
      <AlertDialog
        isOpen={notify.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeNotify}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent maxW="md">
            <AlertDialogHeader
              fontSize="lg"
              fontWeight="bold"
              color={
                notify.status === "success"
                  ? "green.600"
                  : notify.status === "error"
                  ? "red.600"
                  : notify.status === "warning"
                  ? "orange.600"
                  : "blue.600"
              }
            >
              {notify.title}
            </AlertDialogHeader>
            <AlertDialogBody py={4}>{notify.message}</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={closeNotify} colorScheme="blue">
                ตกลง
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default VaccineAdjustmentForm;
